"use client";

import { useEffect, useState } from "react";
import { useRouter } from "next/navigation";
import { Send } from "lucide-react";
import type { Course } from "@/lib/types";
import { Button } from "@/components/ui/Button";
import { useToast } from "@/components/portal/Toast";
import { readSession } from "@/lib/portal/session";
import { ApiError, apiMyCourses, apiRequestEnrollment } from "@/lib/portal/api";

/**
 * Enroll on a website course.
 * - Not signed in → the enquiry (admissions) page for that course.
 * - Signed-in student → sends an enrolment request, same as the dashboard did.
 */
// One lookup per token, shared by every EnrollButton on the page.
let enrolledCache: { token: string; ids: Promise<Set<number>> } | null = null;

function loadEnrolledIds(token: string): Promise<Set<number>> {
  if (enrolledCache?.token !== token) {
    const ids = apiMyCourses(token)
      .then((list) => new Set(list.map((c) => c.course.id)))
      .catch(() => {
        enrolledCache = null;
        return new Set<number>();
      });
    enrolledCache = { token, ids };
  }
  return enrolledCache.ids;
}

export function EnrollButton({ course, size = "md", className }: { course: Course; size?: "sm" | "md" | "lg"; className?: string }) {
  const router = useRouter();
  const toast = useToast();
  const [sending, setSending] = useState(false);
  const [enrolled, setEnrolled] = useState(false);

  useEffect(() => {
    const session = readSession();
    if (!session || session.role !== "student" || !course.id) return;
    let cancelled = false;
    loadEnrolledIds(session.token).then((ids) => {
      if (!cancelled && ids.has(course.id as number)) setEnrolled(true);
    });
    return () => {
      cancelled = true;
    };
  }, [course.id]);

  async function enroll() {
    const session = readSession();
    if (!session) {
      router.push(`/contact?tab=enquiry&form=${course.enquiryForm ?? "general"}&course=${course.slug}`);
      return;
    }
    if (session.role !== "student") {
      toast.info("Students only", "Staff accounts can't enrol in courses.");
      return;
    }
    if (!course.id) {
      router.push(`/contact?tab=enquiry&form=${course.enquiryForm ?? "general"}&course=${course.slug}`);
      return;
    }
    setSending(true);
    try {
      const { message } = await apiRequestEnrollment(session.token, course.id);
      toast.success("Request sent", message);
    } catch (e) {
      toast.error("Could not send request", e instanceof ApiError ? e.message : "Unexpected error.");
    } finally {
      setSending(false);
    }
  }

  if (enrolled) return null;

  return (
    <Button type="button" size={size} onClick={enroll} disabled={sending} className={className}>
      <Send className="h-4 w-4" /> {sending ? "Sending…" : "Enquire Now"}
    </Button>
  );
}
